/**
 * THE STARTING GRID — and the men who did not make it.
 *
 * STEP SIX rounds the field out to 43 (RWEG/p.4), which means a licensed roster
 * of 53 sends ten drivers home every week. Those ten are not a rounding error:
 * failing to qualify is a real outcome with a real cost to a career, so the
 * bubble and the DNQ list are printed here beside the grid rather than dropped
 * on the floor.
 *
 * Nothing on this screen is computed. Positions, chips, the TV list, feuds and
 * the fume list all arrive on `RaceWeekResult`; this file lays them out.
 */

import type { DriverCard } from '../domain/types.ts';
import type { Livery } from '../domain/livery.ts';
import type { GridRow } from './raceWeekModel.ts';
import CarTopDown from './CarTopDown.tsx';
import { logoForTeam, useLogos } from '../lib/logoStore.ts';
import { ordinal } from './boardModel.ts';
import { useCiteText } from '../lib/prefs.ts';

export interface Tag {
  label: string;
  tone?: 'tv' | 'chip' | 'feud' | 'fume' | 'quiet';
  title?: string;
}

export interface DriverTileProps {
  card: DriverCard;
  livery: Livery;
  teamName?: string;
  /** Starting position, when the tile is on the grid. */
  position?: number;
  /** The pole sitter, drawn a size up with the hot edge. */
  pole?: boolean;
  tags?: readonly Tag[];
  /** Performance chips in hand at the green. Drawn as tokens on the car. */
  chips?: number;
  /** Small, for the entry list where fifty-three of these sit at once. */
  compact?: boolean;
  /**
   * UI-008 — open this driver's dossier.
   *
   * Unlike a board card, a grid tile is a plain element in document flow with
   * nothing tweening it, so the whole tile becomes a button: one control, one
   * accessible name, pointer and keyboard through the same door. Omit it and
   * the tile is inert exactly as it was.
   */
  onOpen?: (driverId: string) => void;
}

/**
 * One driver, as the card prints him: the car from above with its number on the
 * roof, his name, his shop, his pit crew's trophy grade and the two pedals.
 *
 * The trophy is a coloured chip AND the metal in words — colour is never the
 * only thing saying anything here.
 */
export function DriverTile({
  card,
  livery,
  teamName,
  position,
  pole = false,
  tags = [],
  chips = 0,
  compact = false,
  onOpen,
}: DriverTileProps) {
  const ct = useCiteText();
  // The shop's mark for the hood, by name. `teamName` is already the shop's
  // name on this tile, so there is nothing to thread — an independent, or a
  // shop with no mark, simply gets a plain hood.
  const hoodLogo = logoForTeam(useLogos(), teamName ?? null);
  return (
    <article
      className={
        `rwtile${pole ? ' rwtile--pole' : ''}${compact ? ' rwtile--compact' : ''}` +
        `${onOpen ? ' rwtile--openable' : ''}`
      }
    >
      {onOpen && (
        <button
          type="button"
          className="rwtile__hit"
          onClick={() => onOpen(card.driverId)}
        >
          <span className="sr-only">
            {card.name}
            {position !== undefined ? `, starting ${ordinal(position)}` : ''} — open dossier
          </span>
        </button>
      )}
      {position !== undefined && (
        <span className="rwtile__pos">
          {ordinal(position)}
          {pole && <b className="rwtile__pole">pole</b>}
        </span>
      )}

      <div className="rwtile__stock">
        <CarTopDown
          livery={livery}
          carNumber={card.carNumber}
          size={pole ? 132 : 108}
          hoodLogoUrl={hoodLogo?.url ?? null}
          hoodLogoLabel={hoodLogo?.label ?? null}
        />
        {/*
          THE TWO TRIANGLES — D-EZ-76. Red points DOWN and is a warning (a red 6
          brings a PROBLEM, RB/xvi); green points UP and is the 2025 set's
          exemption (a failed REPAIR does not end his race). They are drawn on
          the entry list as well as on the boards because this is the screen a
          player reads the field on before he has picked anybody to watch.
        */}
        {card.triangle && (
          <span className="rwtile__tri" title={ct('a red 6 in any challenge or duel brings an immediate PROBLEM (RB/xvi)')} />
        )}
        {card.dnfExempt && (
          <span
            className="rwtile__ok"
            title="green thumbs-up — a failed REPAIR on a PROBLEM does not put him out, but he can no longer challenge or defend (2025 set)"
          />
        )}
        {chips > 0 && (
          <span className="rwtile__chips" title={`${chips} performance chip${chips === 1 ? '' : 's'}`}>
            {Array.from({ length: chips }, (_, i) => (
              <i key={i} className="rwtile__chip" />
            ))}
          </span>
        )}
      </div>

      <h4 className="rwtile__name">{card.name}</h4>
      <p className="rwtile__team">
        {teamName ?? <em>independent</em>}
        <span className="rwtile__no">car {card.carNumber}</span>
      </p>

      <p className="rwtile__stats">
        <span className={`pip${card.trophy ? ` pip--${card.trophy}` : ''}`} aria-hidden="true">
          {card.pitCrewGrade}
        </span>
        <span className="rwtile__grade">
          crew {card.pitCrewGrade}
          {card.trophy ? ` · ${card.trophy}` : ' · no trophy'}
        </span>
        <span className="rwtile__nums">
          gas {card.gasPedal} · brake {card.brakePedal} · fast pit {card.fastPit} · escape {card.escape}
        </span>
      </p>

      {card.qualities.length > 0 && (
        <p className="rwtile__q">
          {card.qualities.map((q) => (card.starred.includes(q) ? `${q}★` : q)).join('  ')}
        </p>
      )}

      {tags.length > 0 && (
        <ul className="rwtile__tags">
          {tags.map((tag) => (
            <li key={tag.label} className={`rwtag${tag.tone ? ` rwtag--${tag.tone}` : ''}`} title={tag.title}>
              {tag.label}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

export interface StartingGridProps {
  rows: readonly GridRow[];
  dnq: readonly string[];
  bubble: readonly string[];
  cards: ReadonlyMap<string, DriverCard>;
  liveries: ReadonlyMap<string, Livery>;
  teamNames: ReadonlyMap<string, string>;
  /** How many drivers held a licence this week. */
  rosterSize: number;
  /**
   * UI-008 — open a driver's dossier. Wired to the grid tiles AND to the two
   * lists of men who went home: a driver who failed to qualify has a card and a
   * career, and being unable to look at him is exactly the treatment this
   * screen's own header argues against.
   */
  onSelectDriver?: (driverId: string) => void;
}

export default function StartingGrid({
  rows,
  dnq,
  bubble,
  cards,
  liveries,
  teamNames,
  rosterSize,
  onSelectDriver,
}: StartingGridProps) {
  const nameOf = (id: string) => cards.get(id)?.name ?? id;
  const pole = rows[0];

  return (
    <section className="rwgrid" aria-labelledby="rwgrid-title">
      <header className="rwsec__head">
        <p className="kicker">Step six · the field is rounded out</p>
        <h2 id="rwgrid-title">The starting grid</h2>
        <p className="rwsec__sub">
          {rows.length} cars take the green from a licensed roster of {rosterSize}.{' '}
          {pole ? (
            <>
              <b>{pole.card.name}</b> is on the pole.
            </>
          ) : (
            'Nobody qualified.'
          )}{' '}
          {dnq.length + bubble.length > 0 && (
            <>
              {dnq.length + bubble.length} went home — {dnq.length} failed to qualify,{' '}
              {bubble.length} left on the bubble.
            </>
          )}
        </p>
      </header>

      <ol className="rwgrid__cards">
        {rows.map((row) => {
          const tags: Tag[] = [];
          if (row.onTv) {
            tags.push({
              label: row.carriedOverTv ? 'TV · carried over' : 'TV list',
              tone: 'tv',
              title: 'Holds the temporary TV quality this race',
            });
          }
          if (row.chips > 0) {
            tags.push({ label: `${row.chips} chip${row.chips === 1 ? '' : 's'}`, tone: 'chip' });
          }
          for (const other of row.feudWith) {
            tags.push({ label: `feud · ${nameOf(other)}`, tone: 'feud' });
          }
          if (row.fuming) tags.push({ label: 'fuming', tone: 'fume' });
          if (row.placement) tags.push({ label: `placement ${row.placement}`, tone: 'quiet' });
          return (
            <li key={row.card.driverId}>
              <DriverTile
                card={row.card}
                livery={liveries.get(row.card.driverId)!}
                teamName={teamNames.get(row.card.teamId ?? '')}
                position={row.position}
                pole={row.position === 1}
                chips={row.chips}
                tags={tags}
                onOpen={onSelectDriver}
              />
            </li>
          );
        })}
      </ol>

      <div className="rwgrid__out">
        <section className="rwout rwout--dnq">
          <h3>Failed to qualify</h3>
          <p className="rwout__note">
            PLACEMENT D with no trophy, or a slow week with no trophy. They do not race.
          </p>
          {dnq.length === 0 ? (
            <p className="rwout__none">Nobody. Every driver who tried made the show or the bubble.</p>
          ) : (
            <ul className="rwout__list">
              {dnq.map((id) => (
                <li key={id}>
                  <span className="rwout__no">{cards.get(id)?.carNumber ?? '—'}</span>
                  {onSelectDriver ? (
                    <button type="button" className="linkish" onClick={() => onSelectDriver(id)}>
                      {nameOf(id)}
                    </button>
                  ) : (
                    nameOf(id)
                  )}
                  <span className="rwout__team">{teamNames.get(cards.get(id)?.teamId ?? '') ?? 'independent'}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rwout rwout--bubble">
          <h3>Left on the bubble</h3>
          <p className="rwout__note">
            They qualified into the bubble stack and the field reached {rows.length} without them.
          </p>
          {bubble.length === 0 ? (
            <p className="rwout__none">The bubble emptied — every man on it got in.</p>
          ) : (
            <ul className="rwout__list">
              {bubble.map((id) => (
                <li key={id}>
                  <span className="rwout__no">{cards.get(id)?.carNumber ?? '—'}</span>
                  {onSelectDriver ? (
                    <button type="button" className="linkish" onClick={() => onSelectDriver(id)}>
                      {nameOf(id)}
                    </button>
                  ) : (
                    nameOf(id)
                  )}
                  <span className="rwout__team">{teamNames.get(cards.get(id)?.teamId ?? '') ?? 'independent'}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </section>
  );
}
